import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Checkbox } from '../Checkbox';
import { Radio } from '../Radio';
import { Tooltip } from '../Tooltip';
import { theme } from '../../theme';
import { Wrapper, WrapperBordered, Table, Head, Body, Row, Cell, HeaderCell, HeaderCellLayout, Footer, TooltipContent, } from './styled';
import { SpecificState } from './SpecificState';
import { ClickableCell, EditableCell } from './cells';
import { SkeletonCell } from './cells/SkeletonCell';
import { defaultEmptyState, defaultErrorState, ScrollPosition } from './utils/constants';
import { StickyHeader } from './StickyHeader';
import { convertFixedColumnInlineStyleToStyled } from './utils';
import { SkeletonHeaderCell } from './cells/SkeletonHeaderCell';
import { SkeletonContent } from './SkeletonContent';
import { DoubleArrowIcon } from '../../tempIcons/DoubleArrowIconIcon';
import { DoubleArrowAscIcon } from '../../tempIcons/DoubleArrowAscIcon';
import { DoubleArrowDescIcon } from '../../tempIcons/DoubleArrowDescIcon';
export const IconUnsorted = () => (React.createElement(DoubleArrowIcon, { width: theme.sizes.base, height: theme.sizes.base, color: "lightgrey" }));
export const IconSortAsc = () => (React.createElement(DoubleArrowAscIcon, { width: theme.sizes.base, height: theme.sizes.base, color: "lightgrey" }));
export const IconSortDesc = () => (React.createElement(DoubleArrowDescIcon, { width: theme.sizes.base, height: theme.sizes.base, color: "lightgrey" }));
export const DataTable = ({ borderless = false, striped = true, noWrap = false, showScrollbar = false, headers, rows, renderRow, renderNestedRow, expandedRowIds, renderLoadingRow, renderFooterRow, renderFooterLoadingRow, omitFooterSelectionCell = false, selectedRowIds, disabledSelectionInputRowIds, selectionMode = 'multiple', onChangeSelectedRowIds, sortColumnId, sortDirection, onChangeSort, isError = false, isLoading = false, skeletonRowsNumber = 5, isHeaderSticky = true, stickyHeaderStyle, customSpecificState, bodyHeightInSpecificStates = 375, startFixedColumnsNumber, endFixedColumnsNumber, }) => {
    const WrapperComponent = borderless ? Wrapper : WrapperBordered;
    const isEmpty = rows ? rows.length === 0 : false;
    const isFixedLayout = Boolean(startFixedColumnsNumber || endFixedColumnsNumber);
    const isFixedColumnsNotEnabled = !startFixedColumnsNumber && !endFixedColumnsNumber;
    const tableRef = useRef(null);
    const wrapperRef = useRef(null);
    const selectionColumnWidth = 58;
    const [scrollPosition, setScrollPosition] = useState(ScrollPosition.none);
    useEffect(() => {
        var _a;
        let refValue = null;
        const handleScroll = () => {
            if (!wrapperRef.current || isFixedColumnsNotEnabled)
                return;
            const el = wrapperRef.current;
            const scrollWidth = el.scrollWidth + 2; // 2 here is 2px which adds by border
            const { scrollLeft } = el;
            const { width } = el.getBoundingClientRect();
            const scrollValue = scrollLeft + width;
            if (scrollWidth === width)
                setScrollPosition(ScrollPosition.none);
            else if (scrollLeft === 0)
                setScrollPosition(ScrollPosition.start);
            else if (scrollLeft > 0 && scrollValue < scrollWidth)
                setScrollPosition(ScrollPosition.middle);
            else if (scrollValue === scrollWidth)
                setScrollPosition(ScrollPosition.end);
        };
        if (wrapperRef.current) {
            refValue = wrapperRef.current;
            handleScroll();
            (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('scroll', handleScroll);
        }
        return () => {
            refValue === null || refValue === void 0 ? void 0 : refValue.removeEventListener('scroll', handleScroll);
        };
    }, [tableRef, isFixedColumnsNotEnabled]);
    const castWidthToNumber = useCallback((width) => {
        if (typeof width === 'string') {
            // eslint-disable-next-line no-console
            console.error("You can only use a numeric value in the 'width' property when you use fixed columns");
            return null;
        }
        if (width === undefined) {
            // eslint-disable-next-line no-console
            console.error("You should provide numeric value in the 'width' attribute in all fixed columns when you use fixed columns");
            return null;
        }
        return width;
    }, []);
    const generateFixedColumnInlineStyle = useCallback((index, offset, side) => ({
        index: side === 'left' ? index : -index,
        style: {
            position: 'sticky',
            [side]: offset,
            zIndex: 1,
        },
    }), []);
    const generateFixedColumnsStyle = useCallback(() => {
        if (!headers || isLoading)
            return null;
        const startColumns = [];
        const endColumns = [];
        let totalStartOffset = 0;
        let totalEndOffset = 0;
        if (startFixedColumnsNumber) {
            if (selectedRowIds) {
                startColumns.push(generateFixedColumnInlineStyle(1, totalStartOffset, 'left'));
                totalStartOffset += selectionColumnWidth;
            }
            for (let i = 0; i < startFixedColumnsNumber; i += 1) {
                const headerWidth = castWidthToNumber(headers[i].width);
                if (headerWidth) {
                    startColumns.push(generateFixedColumnInlineStyle(selectedRowIds ? i + 2 : i + 1, totalStartOffset, 'left'));
                    totalStartOffset += headerWidth;
                }
            }
        }
        if (endFixedColumnsNumber) {
            for (let i = 0; i < endFixedColumnsNumber; i += 1) {
                const headerWidth = castWidthToNumber(headers.slice(-i - 1)[0].width);
                if (headerWidth) {
                    endColumns.push(generateFixedColumnInlineStyle(i + 1, totalEndOffset, 'right'));
                    totalEndOffset += headerWidth;
                }
            }
        }
        if (startColumns.length === 0 && endColumns.length === 0)
            return null;
        return [...startColumns, ...endColumns];
    }, [
        isLoading,
        castWidthToNumber,
        endFixedColumnsNumber,
        generateFixedColumnInlineStyle,
        headers,
        startFixedColumnsNumber,
        selectedRowIds,
    ]);
    const fixedColumnsStyle = useMemo(() => generateFixedColumnsStyle(), [generateFixedColumnsStyle]);
    const handleSelectRow = useCallback((checked, row) => {
        if (!selectedRowIds || !onChangeSelectedRowIds)
            return;
        if (checked) {
            if (selectionMode === 'single') {
                onChangeSelectedRowIds([row.id]);
                return;
            }
            onChangeSelectedRowIds([...selectedRowIds, row.id]);
            return;
        }
        const newSelectedRowIds = [...selectedRowIds];
        const index = newSelectedRowIds.findIndex((id) => id === row.id);
        newSelectedRowIds.splice(index, 1);
        onChangeSelectedRowIds(newSelectedRowIds);
    }, [onChangeSelectedRowIds, selectedRowIds, selectionMode]);
    const handleSelectAll = useCallback(() => {
        if (!selectedRowIds || !onChangeSelectedRowIds)
            return;
        if (selectedRowIds.length === rows.length) {
            onChangeSelectedRowIds([]);
            return;
        }
        const rowIds = rows.map((row) => row.id);
        onChangeSelectedRowIds(rowIds);
    }, [onChangeSelectedRowIds, rows, selectedRowIds]);
    const handleClickSortableHeader = useCallback((columnId) => {
        if (sortColumnId === undefined || onChangeSort === undefined)
            return;
        let nextColumnId = sortColumnId;
        let nextDirection = 'ascending';
        if (sortDirection === 'ascending') {
            nextDirection = 'descending';
        }
        if (columnId !== sortColumnId) {
            nextColumnId = columnId;
            nextDirection = 'ascending';
        }
        onChangeSort(nextColumnId, nextDirection);
    }, [onChangeSort, sortColumnId, sortDirection]);
    const renderHeaderCell = useCallback((header, style) => {
        let className = '';
        let onClick = () => { };
        let sortIcon = React.createElement(IconUnsorted, null);
        const { tooltip, align, icon, title, sortable, id } = header;
        if (sortable) {
            className = 'sortable';
            onClick = () => handleClickSortableHeader(id);
            if (sortColumnId === id) {
                sortIcon = sortDirection === 'ascending' ? React.createElement(IconSortAsc, null) : React.createElement(IconSortDesc, null);
            }
        }
        const headerContents = (React.createElement(HeaderCellLayout, { align: align },
            icon && icon,
            title && React.createElement("span", null, title),
            sortable && sortIcon));
        return (React.createElement(HeaderCell, { key: id, id: `data-table-header-${id}`, className: className, onClick: onClick, align: header.align, grow: header.grow, style: Object.assign({ width: header.width || 'auto' }, style) }, tooltip ? (React.createElement(Tooltip, { text: tooltip },
            React.createElement(TooltipContent, null, headerContents))) : (headerContents)));
    }, [handleClickSortableHeader, sortColumnId, sortDirection]);
    const renderSkeletonHeaderCell = useCallback((header, style) => (React.createElement(SkeletonHeaderCell, { key: header.id, width: header.width || 'auto', style: style, isLoading: isLoading })), [isLoading]);
    const renderHeader = useCallback(() => {
        if (!headers)
            return null;
        const styles = generateFixedColumnsStyle();
        const selectionCellStyle = styles ? styles[0].style : {};
        return (React.createElement(Head, null,
            React.createElement(Row, null,
                selectedRowIds && (React.createElement(HeaderCell, { style: Object.assign({ width: selectionColumnWidth }, selectionCellStyle) }, selectionMode === 'multiple' && (React.createElement(Checkbox, { checked: (rows.length > 0 && (selectedRowIds === null || selectedRowIds === void 0 ? void 0 : selectedRowIds.length) === rows.length) || false, onChange: handleSelectAll, disabled: (disabledSelectionInputRowIds && disabledSelectionInputRowIds.length > 0) ||
                        isLoading ||
                        isEmpty, ariaLabel: "Select all" })))),
                headers.map((header, index) => {
                    const startHeaderIndex = selectedRowIds ? index + 2 : index + 1;
                    const endHeaderIndex = index - headers.length;
                    const headerCellStyle = styles === null || styles === void 0 ? void 0 : styles.find((style) => style.index === startHeaderIndex || style.index === endHeaderIndex);
                    return header.skeleton && (isLoading || isError || isEmpty)
                        ? renderSkeletonHeaderCell(header, header.skeletonStyle || {})
                        : renderHeaderCell(header, (headerCellStyle === null || headerCellStyle === void 0 ? void 0 : headerCellStyle.style) || {});
                }))));
    }, [
        generateFixedColumnsStyle,
        handleSelectAll,
        headers,
        isEmpty,
        isError,
        isLoading,
        renderSkeletonHeaderCell,
        renderHeaderCell,
        rows.length,
        selectedRowIds,
        selectionMode,
        disabledSelectionInputRowIds,
    ]);
    const renderSelectionInput = useCallback((isSelected, row) => {
        const inputProps = {
            checked: isSelected,
            onChange: (checked) => handleSelectRow(checked, row),
            disabled: (disabledSelectionInputRowIds === null || disabledSelectionInputRowIds === void 0 ? void 0 : disabledSelectionInputRowIds.includes(row.id)) || false,
        };
        if (selectionMode === 'single') {
            return React.createElement(Radio, Object.assign({}, inputProps));
        }
        return React.createElement(Checkbox, Object.assign({}, inputProps));
    }, [handleSelectRow, selectionMode, disabledSelectionInputRowIds]);
    const renderSkeletonSelectionInput = useCallback((rowIndex) => {
        const generalStyle = {
            width: 18,
            height: 18,
            margin: 3,
            animationDuration: '0s',
        };
        if (selectionMode === 'single') {
            return (React.createElement(SkeletonCell, { key: rowIndex === undefined ? null : `radio-skeleton-${rowIndex}`, style: Object.assign(Object.assign({}, generalStyle), { borderRadius: '50%' }) }));
        }
        return (React.createElement(SkeletonCell, { key: rowIndex === undefined ? null : `checkbox-skeleton-${rowIndex}`, style: Object.assign(Object.assign({}, generalStyle), { borderRadius: '0.25rem' }) }));
    }, [selectionMode]);
    const getNestedRow = useCallback((nestedRow, isHidden) => {
        var _a, _b;
        if (!renderNestedRow)
            return null;
        const content = renderNestedRow(nestedRow);
        if (!content)
            return null;
        const nestedRowE2eClassName = ((_b = (_a = nestedRow === null || nestedRow === void 0 ? void 0 : nestedRow.data) === null || _a === void 0 ? void 0 : _a.e2eClassNames) === null || _b === void 0 ? void 0 : _b.row) || '';
        return (React.createElement(Row, { key: nestedRow.id, className: `${nestedRowE2eClassName} ${isHidden ? 'hidden' : ''}` },
            selectedRowIds && React.createElement(Cell, null),
            content));
    }, [renderNestedRow, selectedRowIds]);
    const renderDefaultLoadingRow = useCallback((rowIndex) => {
        if (!headers)
            return React.createElement(SkeletonCell, null);
        // Otherwise render a cell for each column
        const cells = headers.map((header) => React.createElement(SkeletonCell, { key: `${rowIndex}-${header.id}` }));
        if (selectedRowIds)
            cells.unshift(renderSkeletonSelectionInput(rowIndex));
        return cells;
    }, [headers, renderSkeletonSelectionInput, selectedRowIds]);
    const getFooter = () => {
        if (!renderFooterRow)
            return null;
        if (isLoading && !renderFooterLoadingRow)
            return null;
        if (!isLoading && isEmpty)
            return null;
        return (React.createElement(Footer, { fixedColumnsStyle: convertFixedColumnInlineStyleToStyled(fixedColumnsStyle || []) },
            React.createElement(Row, null,
                selectedRowIds && !omitFooterSelectionCell && React.createElement(Cell, null),
                !isLoading && renderFooterRow(rows),
                isLoading && renderFooterLoadingRow && renderFooterLoadingRow())));
    };
    let lastStartFixedColumnsIndex = startFixedColumnsNumber;
    if (selectedRowIds && lastStartFixedColumnsIndex)
        lastStartFixedColumnsIndex += 1;
    const renderedRows = useMemo(() => rows.map((row) => {
        var _a, _b, _c;
        const { data } = row;
        const isSelected = selectedRowIds ? selectedRowIds.includes(row.id) : false;
        const isExpanded = expandedRowIds ? expandedRowIds.includes(row.id) : true;
        const bodyClassName = (_a = data === null || data === void 0 ? void 0 : data.e2eClassNames) === null || _a === void 0 ? void 0 : _a.body;
        const rowE2eClassName = (_c = (_b = data === null || data === void 0 ? void 0 : data.e2eClassNames) === null || _b === void 0 ? void 0 : _b.row) !== null && _c !== void 0 ? _c : '';
        const rowClassName = isSelected ? `selected ${rowE2eClassName}` : `${rowE2eClassName}`;
        return (React.createElement(Body, { key: row.id, className: bodyClassName, fixedColumnsStyle: convertFixedColumnInlineStyleToStyled(fixedColumnsStyle || []) },
            React.createElement(Row, { key: row.id, className: rowClassName, accentColor: row.accentColor },
                selectedRowIds && React.createElement(Cell, null, renderSelectionInput(isSelected, row)),
                renderRow(row)),
            row.nestedRows && row.nestedRows.map((item) => getNestedRow(item, !isExpanded))));
    }), [
        rows,
        expandedRowIds,
        fixedColumnsStyle,
        getNestedRow,
        renderRow,
        renderSelectionInput,
        selectedRowIds,
    ]);
    /* Loading state */
    if (isLoading) {
        const loadingRows = [];
        for (let i = 0; i < skeletonRowsNumber; i += 1) {
            loadingRows.push(React.createElement(Body, { key: `loading-row-${i}`, fixedColumnsStyle: convertFixedColumnInlineStyleToStyled(fixedColumnsStyle || []) },
                React.createElement(Row, null, renderLoadingRow ? (React.createElement(React.Fragment, null,
                    selectedRowIds && renderSkeletonSelectionInput(),
                    renderLoadingRow())) : (renderDefaultLoadingRow(i)))));
        }
        return (React.createElement(WrapperComponent, { ref: wrapperRef, scrollDisabled: true, scrollPosition: scrollPosition, lastStartFixedColumnsIndex: lastStartFixedColumnsIndex, firstEndFixedColumnsIndex: endFixedColumnsNumber },
            isHeaderSticky && headers && (React.createElement(StickyHeader, { renderHeader: renderHeader, tableRef: tableRef, wrapperRef: wrapperRef, isFixedLayout: isFixedLayout, wrapperStyle: stickyHeaderStyle })),
            React.createElement(Table, { ref: tableRef, striped: striped, noWrap: noWrap, tableLoading: true, isFixedLayout: isFixedLayout },
                renderHeader(),
                loadingRows,
                getFooter())));
    }
    /* Empty & Error states */
    if (isEmpty || isError) {
        let specificStateValues = defaultEmptyState;
        if (customSpecificState)
            specificStateValues = customSpecificState;
        else if (isError)
            specificStateValues = defaultErrorState;
        return (React.createElement(WrapperComponent, { scrollDisabled: true },
            React.createElement(Table, { ref: tableRef, striped: striped, noWrap: noWrap, tableLoading: false, isFixedLayout: isFixedLayout },
                renderHeader(),
                getFooter()),
            React.createElement(SpecificState, Object.assign({ height: bodyHeightInSpecificStates }, specificStateValues))));
    }
    /* Populated state */
    return (React.createElement(WrapperComponent, { scrollDisabled: false, ref: wrapperRef, scrollPosition: scrollPosition, lastStartFixedColumnsIndex: lastStartFixedColumnsIndex, firstEndFixedColumnsIndex: endFixedColumnsNumber, showScrollbar: showScrollbar },
        isHeaderSticky && headers && (React.createElement(StickyHeader, { renderHeader: renderHeader, tableRef: tableRef, wrapperRef: wrapperRef, isFixedLayout: isFixedLayout, wrapperStyle: stickyHeaderStyle })),
        React.createElement(Table, { ref: tableRef, striped: striped, noWrap: noWrap, tableLoading: false, isFixedLayout: isFixedLayout },
            renderHeader(),
            renderedRows,
            getFooter())));
};
DataTable.Cell = Cell;
DataTable.ClickableCell = ClickableCell;
DataTable.EditableCell = EditableCell;
DataTable.SkeletonCell = SkeletonCell;
DataTable.SkeletonContent = SkeletonContent;
//# sourceMappingURL=DataTable.js.map